import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

/**
 * The one modal primitive. Every overlay in the existing UI is this shape — backdrop,
 * a header with a ✕, a body, and an action row — so they share one implementation
 * instead of eight near-identical blocks (docs/UI-COMPONENTS.md §3.2).
 *
 * PORTALED TO BODY, not rendered where it is summoned — owner, 2026-08-11: button
 * 的文字超出 button 繪製的範圍了 (screenshot). The queue sheet is summoned from the
 * composer row, and `#footbtns button` is an ID-strength rule that turns every
 * button into a 40px circle — including, back then, the sheet's own "Deliver now" /
 * "Discard all", whose labels spilled straight past the circle. A modal's styling
 * must not depend on which corner of the page summoned it; the scrim is
 * `position: fixed`, so rendering from body paints identically and also escapes any
 * transformed/overflow ancestor.
 */
export function Sheet({
  title,
  head,
  onClose,
  children,
  actions,
  className,
  docked = false,
}: {
  /** The dialog's accessible name, and the title bar's text unless `head` is given. */
  title: string
  /**
   * What the title bar shows INSTEAD of the plain title — the agent card puts its
   * editable name there (owner, 2026-09-09: agent name 出現了三次, 這是要幹麻?), so
   * the title, the identity row and the rename field stop being three copies of one
   * word. `title` stays the accessible name either way.
   */
  head?: ReactNode
  onClose: () => void
  children: ReactNode
  actions?: ReactNode
  /** Modifier for sheets whose content wants different sizing — the note queue
   *  is a LIST, and a list benefits from height in a way a form does not. */
  className?: string
  /**
   * STUCK TO THE BOTTOM of the screen instead of centred — owner, 2026-10-17, on the reactions picker:
   * 「這個彈出頁面可以讓它黏在底部, 這樣子的設計會更符合使用者的習慣」. The phone's own sheets rise
   * from the bottom edge, within the thumb's reach; a picker of many small targets wants that.
   */
  docked?: boolean
}) {
  /*
   * The backdrop closes the sheet on an outside tap — but ONLY when the whole gesture
   * (press AND release) landed on the scrim itself, never on a "fall-through" click.
   *
   * iOS Safari dispatches the click of a DISABLED element THROUGH to whatever sits behind
   * it. A control that disables itself the instant it is tapped (an optimistic mutation's
   * `disabled={isPending}`) therefore hands its click to the scrim, whose bare
   * `e.target === e.currentTarget` was true and closed the sheet — the roster snapping shut
   * the moment the owner tapped mute/listen (2026-08-17, iOS-only, invisible to Chromium).
   * Requiring the PRESS (mousedown / touchstart) to have landed on the scrim too makes a
   * fall-through click — which BEGINS on the button, not the scrim — unable to close
   * anything. Immunises EVERY sheet, including the roster's own detach/invite buttons that
   * disable the same way. (mousedown+touchstart rather than pointerdown: the press must be
   * recorded under both desktop mouse and iOS touch, and both fire deterministically in jsdom
   * so the guard is unit-provable — see Sheet.test.tsx.)
   */
  const downOnScrim = useRef(false)
  return createPortal(
    <div
      className={`sheet-scrim${docked ? ' docked' : ''}`}
      onMouseDown={(e) => {
        downOnScrim.current = e.target === e.currentTarget
      }}
      onTouchStart={(e) => {
        downOnScrim.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        const armed = downOnScrim.current
        downOnScrim.current = false
        if (armed && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={`sheet${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="sheet-hd">
          {head ?? <span className="sheet-title">{title}</span>}
          <button type="button" className="sheet-x" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} label="Close" />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {actions && <div className="sheet-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  )
}

// `Chip` (the selectable pill) left with its last caller, 2026-09-09: the ＋ sheet's
// attach list is the Attach-agents picker's body now (PickerBody), one column with
// the machine at the right. One renderer of "pick agents", not two.
